import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import i18n from '../i18n';
import { authApi } from '../lib/api';

interface User {
  id: string;
  email: string;
  name: string | null;
  role: string;
  org_id: string;
  is_active: boolean;
  must_change_password: boolean;
  is_platform: boolean;
  feature_flags: Record<string, boolean>;
  billing_status?: string;
  language?: string;
  /** ISO 4217 currency code (e.g. 'GBP', 'EUR') from the user's org.
   *  Backend populates this on /auth/me + /auth/login. Used by
   *  lib/currency.ts to render amounts in the customer's own currency
   *  rather than hardcoded £. */
  currency?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const saved = localStorage.getItem('sp_user');
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState<string | null>(localStorage.getItem('sp_token'));
  const [loading, setLoading] = useState(() => {
    // If we already have both token and user from localStorage, no need to load
    return !!(localStorage.getItem('sp_token') && !localStorage.getItem('sp_user'));
  });

  useEffect(() => {
    // Only call /me on initial page load when we have a token but no cached user.
    // Skip if user is already set (e.g. from login() which sets both token and user).
    const cachedUser = localStorage.getItem('sp_user');
    if (token && !cachedUser) {
      authApi
        .me()
        .then((res) => {
          setUser(res.data);
          localStorage.setItem('sp_user', JSON.stringify(res.data));
          if (res.data.language && res.data.language !== i18n.language) {
            i18n.changeLanguage(res.data.language);
          }
        })
        .catch(() => {
          setToken(null);
          setUser(null);
          localStorage.removeItem('sp_token');
          localStorage.removeItem('sp_user');
        })
        .finally(() => setLoading(false));
    } else if (token && cachedUser) {
      // Sync language from cached user on page reload (e.g. after screensaver)
      try {
        const u = JSON.parse(cachedUser);
        if (u.language) i18n.changeLanguage(u.language);
      } catch {
        /* ignore parse errors */
      }
      setLoading(false);
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Periodic token refresh — every 6 hours while authenticated
  const refreshTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const tryRefresh = useCallback(() => {
    authApi
      .refresh()
      .then((res) => {
        const newToken: string = res.data.token;
        localStorage.setItem('sp_token', newToken);
        setToken(newToken);
        // Re-fetch user profile — role, feature flags, or billing status
        // may have changed since last login
        return authApi.me();
      })
      .then((res) => {
        setUser(res.data);
        localStorage.setItem('sp_user', JSON.stringify(res.data));
      })
      .catch(() => {
        /* token still valid or will be caught by interceptor */
      });
  }, []);

  useEffect(() => {
    if (token) {
      const SIX_HOURS = 6 * 60 * 60 * 1000;
      refreshTimer.current = setInterval(tryRefresh, SIX_HOURS);
    }
    return () => {
      if (refreshTimer.current) clearInterval(refreshTimer.current);
    };
  }, [token, tryRefresh]);

  const login = async (email: string, password: string) => {
    const res = await authApi.login(email, password);
    const { token: t, user: u } = res.data;
    localStorage.setItem('sp_token', t);
    localStorage.setItem('sp_user', JSON.stringify(u));
    setToken(t);
    setUser(u);
    // Always sync UI language — ensures sp_language is cached in localStorage
    i18n.changeLanguage(u.language || 'en');
  };

  const logout = () => {
    localStorage.removeItem('sp_token');
    localStorage.removeItem('sp_user');
    setToken(null);
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, token, loading, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
